import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { CalendarCheck, CheckCircle2, FileText, Flame, Sparkles, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import AnimatedNumber from "@/components/AnimatedNumber";
import { cn } from "@/lib/utils";

const SCENE_INTERVAL_MS = 5000;

const documentTypes = ["PDF", "DOCX", "EPUB"];
const quizOptionKeys = [
  "landing.heroIllustration.optionA",
  "landing.heroIllustration.optionB",
  "landing.heroIllustration.optionC",
];

function FlexoMascot({ className = "", floating = true, size = "h-16 md:h-20" }) {
  return (
    <motion.img
      src="/logo-small.v1.webp"
      alt="Flexo mascot"
      className={cn(size, "w-auto drop-shadow-lg", className)}
      width="80"
      height="80"
      decoding="async"
      onError={(event) => {
        event.currentTarget.onerror = null;
        event.currentTarget.src = "/logo-small.png";
      }}
      animate={floating ? { y: [0, -6, 0] } : { y: 0 }}
      transition={{
        duration: 2.8,
        repeat: floating ? Number.POSITIVE_INFINITY : 0,
        ease: "easeInOut",
      }}
    />
  );
}

function QuizScene({ t }) {
  return (
    <>
      <div className="grid w-full max-w-md grid-cols-3 gap-4 pt-2">
        {documentTypes.map((type, index) => (
          <motion.div
            key={type}
            initial={{ opacity: 0, y: -18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1, ease: "easeOut" }}
          >
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{
                duration: 3,
                repeat: Number.POSITIVE_INFINITY,
                ease: "easeInOut",
                delay: index * 0.2,
              }}
              className="rounded-2xl border border-border/70 bg-surface px-4 py-3 shadow-soft"
            >
              <div className="mb-2 flex items-center gap-2">
                <FileText className="h-5 w-5 text-terracotta" />
                <span className="font-heading text-sm font-bold text-foreground">{type}</span>
              </div>
              <p className="font-body text-xs text-muted-foreground">
                {t("landing.heroIllustration.chipHint")}
              </p>
            </motion.div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.5, ease: "easeOut" }}
        className="relative mx-auto mt-6 h-14 w-full max-w-md"
      >
        <div className="absolute left-[18%] top-2 h-12 w-0.5 rotate-[26deg] border-l-2 border-dashed border-terracotta/45" />
        <div className="absolute left-1/2 top-1 h-14 w-0.5 -translate-x-1/2 border-l-2 border-dashed border-terracotta/45" />
        <div className="absolute right-[18%] top-2 h-12 w-0.5 -rotate-[26deg] border-l-2 border-dashed border-terracotta/45" />
        <div className="absolute left-1/2 top-[2.8rem] h-3 w-3 -translate-x-1/2 rounded-full bg-secondary shadow-[0_0_0_5px_rgba(14,165,233,0.18)]" />
      </motion.div>

      <div className="relative mx-auto -mt-4 w-full max-w-sm">
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.9, ease: "easeOut" }}
          className="absolute -top-10 -left-10 z-20 rounded-2xl border border-border/70 bg-surface px-4 py-3 shadow-soft"
        >
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-100">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t("landing.yourProgress")}</p>
              <p className="font-heading text-2xl font-black leading-none text-foreground">+23%</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.7, ease: "easeOut" }}
          className="mt-2 w-full rounded-2xl border border-border/70 bg-surface p-5 shadow-soft"
        >
          <div className="mb-3 flex items-center gap-2 text-secondary">
            <Sparkles className="h-4 w-4" />
            <p className="font-heading text-sm font-bold">
              {t("landing.heroIllustration.quizReady")}
            </p>
          </div>
          <p className="font-body text-sm text-foreground">
            {t("landing.heroIllustration.question")}
          </p>
          <div className="mt-3 space-y-2">
            {quizOptionKeys.map((optionKey) => (
              <div
                key={optionKey}
                className="flex items-center gap-2 rounded-xl border border-border/60 bg-background/75 px-3 py-2"
              >
                <span className="h-3 w-3 rounded-full border border-muted-foreground/70" />
                <span className="font-body text-sm text-muted-foreground">{t(optionKey)}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </>
  );
}

function LevelScene({ t }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-6">
      <FlexoMascot size="h-24 md:h-28" />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15 }}
        className="w-full max-w-sm rounded-2xl border border-border/70 bg-surface px-6 py-5 shadow-soft"
      >
        <div className="mb-1 text-[0.65rem] font-bold uppercase tracking-[0.22em] text-muted-foreground">
          {t("landing.heroIllustration.ability")}
        </div>
        <div className="flex items-baseline gap-1">
          <span className="font-heading text-5xl font-black leading-none text-secondary">
            <AnimatedNumber from={0} to={67} />
          </span>
          <span className="font-heading text-lg font-bold text-muted-foreground">/100</span>
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-secondary/15">
          <motion.div
            className="h-full rounded-full bg-secondary"
            initial={{ width: 0 }}
            animate={{ width: "67%" }}
            transition={{ duration: 1.2, ease: "easeOut" }}
          />
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          {t("landing.heroIllustration.calibrated")}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.35 }}
        className="flex w-full max-w-sm items-center gap-3 rounded-2xl border border-border/70 bg-surface px-4 py-3 shadow-soft"
      >
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-terracotta-light">
          <Flame className="h-6 w-6 text-terracotta" strokeWidth={2.5} />
        </div>
        <div>
          <div className="text-[0.65rem] font-bold uppercase tracking-[0.22em] text-muted-foreground">
            {t("landing.heroIllustration.streakLabel")}
          </div>
          <div className="font-heading text-xl font-black leading-tight text-foreground">
            {t("landing.heroIllustration.streakValue", { count: 8 })}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function ReviewScene({ t }) {
  const topics = [
    { key: "topicPhoto", pct: 92, tone: "mastered" },
    { key: "topicCell", pct: 70, tone: "proficient" },
    { key: "topicGenetics", pct: 45, tone: "familiar" },
  ];
  const toneStyles = {
    mastered: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    proficient: "bg-secondary/15 text-secondary",
    familiar: "bg-terracotta-light text-terracotta",
  };

  return (
    <div className="flex h-full flex-col items-center justify-center gap-5">
      <div className="flex w-full max-w-sm items-center gap-4">
        <FlexoMascot size="h-20 md:h-24" className="shrink-0" />
        <motion.div
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex-1 rounded-2xl border border-border/70 bg-surface px-4 py-3 shadow-soft"
        >
          <div className="mb-1 flex items-center gap-2 text-secondary">
            <CalendarCheck className="h-4 w-4" />
            <span className="font-heading text-[0.65rem] font-bold uppercase tracking-[0.22em]">
              {t("landing.heroIllustration.dueToday")}
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-heading text-3xl font-black leading-none text-foreground">
              <AnimatedNumber from={0} to={3} duration={0.8} />
            </span>
            <span className="font-body text-xs text-muted-foreground">
              {t("landing.heroIllustration.conceptsToReview")}
            </span>
          </div>
        </motion.div>
      </div>

      <div className="w-full max-w-sm space-y-2">
        <div className="text-[0.65rem] font-bold uppercase tracking-[0.22em] text-muted-foreground">
          {t("landing.heroIllustration.masteryHeading")}
        </div>
        {topics.map((topic, index) => (
          <motion.div
            key={topic.key}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
            className="flex items-center justify-between rounded-2xl border border-border/70 bg-surface px-4 py-2.5 shadow-soft"
          >
            <div className="flex items-center gap-2">
              <CheckCircle2
                className={cn(
                  "h-4 w-4",
                  topic.tone === "mastered"
                    ? "text-emerald-600"
                    : topic.tone === "proficient"
                      ? "text-secondary"
                      : "text-terracotta",
                )}
                strokeWidth={2.5}
              />
              <span className="font-heading text-sm font-bold text-foreground">
                {t(`landing.heroIllustration.${topic.key}`)}
              </span>
            </div>
            <span
              className={cn(
                "rounded-full px-2.5 py-0.5 font-heading text-xs font-black",
                toneStyles[topic.tone],
              )}
            >
              <AnimatedNumber from={0} to={topic.pct} />%
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

const scenes = [
  { id: "quiz", Component: QuizScene },
  { id: "level", Component: LevelScene },
  { id: "review", Component: ReviewScene },
];

export default function HeroIllustration() {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % scenes.length);
    }, SCENE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [reduceMotion]);

  const CurrentScene = scenes[index].Component;

  return (
    <div className="relative h-[500px] overflow-hidden rounded-3xl border border-border/60 bg-sand-dark p-8 shadow-soft">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(224,82,54,0.15),transparent_48%),radial-gradient(circle_at_80%_80%,rgba(14,165,233,0.18),transparent_52%)]" />

      <div className="relative z-10 flex h-full flex-col">
        <div className="flex flex-1 flex-col overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={scenes[index].id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
              className="flex flex-1 flex-col"
            >
              <CurrentScene t={t} />
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-center gap-2 pt-4">
          {scenes.map((scene, i) => (
            <button
              key={scene.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={t("landing.heroIllustration.sceneDot", { index: i + 1 })}
              className={cn(
                "h-1.5 rounded-full transition-all",
                i === index ? "w-6 bg-terracotta" : "w-1.5 bg-muted-foreground/30",
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
